<template>
    <kt-modal 
        :visible="visible" 
        title="Manage Vehicles"
        size="lg"
        @dismiss="$emit('close')"
    >
        <template #body>
            <div class="vehicle-selector">
                <!-- Add New Vehicle Form (Admin only) -->
                <div v-if="props.canManage" class="card mb-3">
                    <div class="card-header py-2">
                        <strong>{{ editingVehicle ? 'Edit Vehicle' : 'Add New Vehicle' }}</strong>
                    </div>
                    <div class="card-body py-2">
                        <div class="row g-2">
                            <div class="col-md-10">
                                <input 
                                    type="text" 
                                    class="form-control form-control-sm" 
                                    v-model="newVehicle.name"
                                    placeholder="Vehicle Name (e.g., Company Truck, Personal Car)"
                                />
                            </div>
                            <div class="col-md-2">
                                <button 
                                    type="button"
                                    class="btn btn-sm btn-primary w-100"
                                    @click="saveVehicle"
                                    :disabled="!newVehicle.name"
                                >
                                    <Save :size="14" class="me-1" />
                                    {{ editingVehicle ? 'Update' : 'Add' }}
                                </button>
                            </div>
                        </div>
                        <div v-if="editingVehicle" class="mt-2">
                            <button 
                                type="button"
                                class="btn btn-sm btn-outline-secondary"
                                @click="cancelEdit"
                            >
                                Cancel Edit
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Vehicle List -->
                <div class="card">
                    <div class="card-header py-2">
                        <strong>Select a Vehicle</strong>
                    </div>
                    <div class="card-body p-0">
                        <div v-if="loading" class="text-center py-3">
                            <span class="spinner-border spinner-border-sm"></span>
                            Loading vehicles...
                        </div>
                        <div v-else-if="vehicles.length === 0" class="text-center py-3 text-muted">
                            No vehicles configured. Add one above.
                        </div>
                        <table v-else class="table table-sm table-hover mb-0">
                            <thead>
                                <tr>
                                    <th>Vehicle Name</th>
                                    <th v-if="props.canManage" style="width: 120px">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr 
                                    v-for="vehicle in vehicles" 
                                    :key="vehicle.name"
                                    :class="{ 'table-primary': selectedVehicle === vehicle.name }"
                                    style="cursor: pointer;"
                                    @click="selectVehicle(vehicle)"
                                >
                                    <td>{{ vehicle.name }}</td>
                                    <td v-if="props.canManage">
                                        <button 
                                            type="button"
                                            class="btn btn-sm btn-outline-primary me-1"
                                            @click.stop="editVehicle(vehicle)"
                                            title="Edit"
                                        >
                                            <Edit :size="14" />
                                        </button>
                                        <button 
                                            type="button"
                                            class="btn btn-sm btn-outline-danger"
                                            @click.stop="deleteVehicle(vehicle)"
                                            title="Delete"
                                        >
                                            <Trash2 :size="14" />
                                        </button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </template>

        <template #footer>
            <button type="button" class="btn btn-secondary" @click="$emit('close')">
                Cancel
            </button>
            <button 
                type="button" 
                class="btn btn-primary"
                @click="confirmSelection"
                :disabled="!selectedVehicle"
            >
                Select Vehicle
            </button>
        </template>
    </kt-modal>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import KtModal from '@/components/UI/KtModal.vue';
import OptionsService from '@/services/OptionsService';
import Alert from '@/services/Alert';
import Toast from '@/services/Toast';
import { Save, Edit, Trash2 } from 'lucide-vue-next';

interface Vehicle {
    name: string;
}

const props = defineProps<{
    visible: boolean;
    currentValue?: string | null;
    canManage?: boolean;
}>();

const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'select', vehicle: Vehicle): void;
    (e: 'vehicles-changed', vehicles: Vehicle[]): void;
}>();

const loading = ref(false);
const vehicles = ref<Vehicle[]>([]);
const selectedVehicle = ref<string | null>(null);
const editingVehicle = ref<Vehicle | null>(null);
const newVehicle = ref<Vehicle>({ name: '' });

const OPTION_KEY = 'vehicles_json';

const loadVehicles = async () => {
    loading.value = true;
    try {
        const optionValue = await OptionsService.get(OPTION_KEY);
        if (optionValue) {
            vehicles.value = JSON.parse(optionValue);
        } else {
            vehicles.value = [];
        }
    } catch (error) {
        console.error('Error loading vehicles:', error);
        vehicles.value = [];
    } finally {
        loading.value = false;
    }
};

const saveVehiclesToOptions = async () => {
    try {
        await OptionsService.set(OPTION_KEY, JSON.stringify(vehicles.value));
        emit('vehicles-changed', vehicles.value);
    } catch (error) {
        console.error('Error saving vehicles:', error);
        Toast.error('Failed to save vehicles');
    }
};

const saveVehicle = async () => {
    if (!newVehicle.value.name.trim()) {
        Toast.error('Vehicle name is required');
        return;
    }

    if (editingVehicle.value) {
        // Update existing
        const index = vehicles.value.findIndex(v => v.name === editingVehicle.value!.name);
        if (index !== -1) {
            vehicles.value[index] = { ...newVehicle.value };
        }
        editingVehicle.value = null;
    } else {
        // Check for duplicate
        if (vehicles.value.some(v => v.name.toLowerCase() === newVehicle.value.name.toLowerCase())) {
            Toast.error('A vehicle with this name already exists');
            return;
        }
        // Add new
        vehicles.value.push({ ...newVehicle.value });
    }

    await saveVehiclesToOptions();
    newVehicle.value = { name: '' };
    Toast.success('Vehicle saved');
};

const editVehicle = (vehicle: Vehicle) => {
    editingVehicle.value = vehicle;
    newVehicle.value = { ...vehicle };
};

const cancelEdit = () => {
    editingVehicle.value = null;
    newVehicle.value = { name: '' };
};

const deleteVehicle = async (vehicle: Vehicle) => {
    Alert.confirm(
        `Are you sure you want to delete "${vehicle.name}"?`,
        async () => {
            vehicles.value = vehicles.value.filter(v => v.name !== vehicle.name);
            await saveVehiclesToOptions();
            Toast.success('Vehicle deleted');
            
            if (selectedVehicle.value === vehicle.name) {
                selectedVehicle.value = null;
            }
        },
        () => {}
    );
};

const selectVehicle = (vehicle: Vehicle) => {
    selectedVehicle.value = vehicle.name;
};

const confirmSelection = () => {
    const vehicle = vehicles.value.find(v => v.name === selectedVehicle.value);
    if (vehicle) {
        emit('select', vehicle);
    }
    emit('close');
};

watch(() => props.visible, (newVal) => {
    if (newVal) {
        loadVehicles();
        selectedVehicle.value = props.currentValue || null;
    }
});

onMounted(() => {
    if (props.visible) {
        loadVehicles();
        selectedVehicle.value = props.currentValue || null;
    }
});
</script>

<style scoped>
.vehicle-selector {
    min-height: 300px;
}
</style>
